/**
 * Logical model for write mutations (insert/update/delete).
 *
 * Mirrors the read {@link QueryPlan} but for mutations. Reuses {@link FilterNode}
 * for the WHERE/query criteria so filters behave identically to reads.
 */
import type { FieldRefNode, FilterNode, SourceNode } from './query-plan';
import { PlanNode } from './query-plan';

/** How an update assignment mutates its target field. */
export type AssignmentOp = 'set' | 'increment' | 'append' | 'removeField';

/** A single update assignment (`set`/`increment`/`append`/`removeField`). */
export class AssignmentNode extends PlanNode {
    readonly kind = 'assignment';
    constructor(
        path: string,
        readonly field: FieldRefNode,
        readonly op: AssignmentOp,
        /** Value to set/increment-by/append; `null` for `removeField`. */
        readonly value: unknown,
    ) {
        super(path);
    }
}

/** A delete/update/insert over one source. */
export class MutationPlan extends PlanNode {
    readonly kind = 'mutation-plan';
    constructor(
        path: string,
        readonly operation: 'delete' | 'update' | 'insert',
        readonly source: SourceNode,
        /** Assignments for `update`; empty otherwise. */
        readonly assignments: AssignmentNode[],
        /** Criteria; `null` means "all rows/documents" (unfiltered / N/A). */
        readonly filter: FilterNode | null,
        /** Ordered column names for `insert`; empty otherwise. */
        readonly columns: string[] = [],
        /** Row values aligned to {@link columns} for `insert`; empty otherwise. */
        readonly rows: unknown[][] = [],
        /**
         * Key columns for an upsert (`insert` with conflict resolution); `null`
         * for a plain insert or non-insert mutations.
         */
        readonly conflictKeys: string[] | null = null,
    ) {
        super(path);
    }
}
